import {Form, Input, InputNumber, Select, Spin} from 'antd';
import {groupThousands, parseThousands} from '../v2/numberFormat';
import styles from './overview.module.scss';
import FadeEffect from 'components/animation/fadeEffect';
import trash from 'images/trash-can-outline.svg';
import plusIcon from 'images/plus-circle.png';
import v2 from '../v2/instrumentV2.module.scss';
import {FormCard} from '../v2/FormCard';
import {InstrumentActions} from '../v2/InstrumentActions';
import {FUND_INVESTMENT_MOCK} from '../v2/devPrefill';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {useDispatch, useSelector} from 'react-redux';
import {handleMutualFunds} from 'redux/store/assetClass';
import {useEffect, useState} from 'react';
import DBModal from 'components/modal/modal';
import {CreateMutualFundsModal} from './CreateMutualFundsModal';
import {GET_MUTUAL_FUND_BY_LANGUAGE} from 'graphql/queries/assetManagement';
import {useParams} from 'react-router-dom';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {handleUpdateMutualFund} from '../actions';
import {UPDATE_MUTUAL_FUND} from 'graphql/mutations/assetManagement';
import {
  NavTabsEnum,
  returnMetrics,
  trailingReturnPeriods,
} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';

// `YTDReturn`, `lastYearReturn` and `historicalPerformance` all collapsed into
// the single `returns` list. An entry with no year is a trailing window; an
// entry with a year is a calendar-year record — the two are never mixed, which
// is why they are edited as two separate lists here and merged on submit.
//
// Dropped by the migration with no replacement: investmentSteps,
// investmentCalculatorSlider, redemptionTerms, capitalizationRate, and the
// whole useOfProceeds block (funds have no offering).
export const MutualFundsInvestment = () => {
  const [createModal, setCreateModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [updatedValues, setUpdatedValues] = useState({});

  const {id, lang} = useParams();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const fieldValues = useSelector(state => state?.assetClass?.mutualFunds);

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  const {
    data,
    refetch,
    loading: loadingInstrument,
  } = useQuery(GET_MUTUAL_FUND_BY_LANGUAGE, {
    variables: {
      getFinancialInstrumentId: id,
      language: lang,
    },
    skip: !id,
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const [updateMutualFund, {loading: loadingUpdate}] = useMutation(
    UPDATE_MUTUAL_FUND,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  // returns[] -> the two editable lists
  const splitReturns = returns => ({
    trailingReturns:
      returns
        ?.filter(entry => entry?.year == null)
        ?.map(entry => ({
          period: entry?.period,
          metric: entry?.metric,
          value: entry?.value,
        })) ?? [],
    calendarReturns:
      returns
        ?.filter(entry => entry?.year != null)
        ?.map(entry => ({
          year: entry?.year,
          metric: entry?.metric,
          value: entry?.value,
        })) ?? [],
  });

  // the two lists -> returns[]
  const mergeReturns = (trailingReturns, calendarReturns) => [
    ...(trailingReturns?.map(entry => ({
      period: entry?.period,
      metric: entry?.metric ?? 'RETURN',
      value: Number(entry?.value),
    })) ?? []),
    ...(calendarReturns?.map(entry => ({
      period: 'YEAR',
      year: Number(entry?.year),
      metric: entry?.metric ?? 'RETURN',
      value: Number(entry?.value),
    })) ?? []),
  ];

  // Only the fields this screen owns. Spreading the whole record into the form
  // store leaks every queried field into onFinish and then into the mutation.
  const applyValues = source => {
    if (!source) return;
    form.setFieldsValue({
      valuationFrequency: source.valuationFrequency,
      settlementDaysOnRedemption: source.settlementDaysOnRedemption,
      projectedAnnualReturn: source.projectedAnnualReturn,
      investmentHorizon: source.investmentHorizon,
      benefits: source.benefits?.map(benefit => ({benefit})) ?? [],
      ...splitReturns(source.returns),
    });
  };

  useEffect(() => {
    applyValues(data?.getFinancialInstrument?.data);
  }, [data?.getFinancialInstrument?.data]);

  useEffect(() => {
    if (fieldValues && !id) applyValues(fieldValues);
  }, [fieldValues]);

  if (loadingInstrument) {
    return (
      <div className="query-loading">
        <Spin />
        <h1>Loading...</h1>
      </div>
    );
  }

  const buildPayload = values => ({
    valuationFrequency: values.valuationFrequency,
    settlementDaysOnRedemption: values.settlementDaysOnRedemption,
    projectedAnnualReturn: values.projectedAnnualReturn,
    // Schema type is String; a prefilled/typed number would otherwise reject.
    investmentHorizon:
      values.investmentHorizon === undefined ||
      values.investmentHorizon === null ||
      values.investmentHorizon === ''
        ? null
        : String(values.investmentHorizon),
    benefits: values.benefits?.map?.(d => d?.benefit) ?? [],
    // `returns` lives on the base instrument input, the rest on the fund input.
    baseFinancialInstrumentInput: {
      returns: mergeReturns(values.trailingReturns, values.calendarReturns),
    },
  });

  return (
    <div>
      <div className={v2.bodyHead}>
        <h2>Investment and performance</h2>
        <p>
          Returns, valuation cadence and what investors get for holding this
          fund.
        </p>
      </div>

      <Form
        onFinish={values => {
          if (id) {
            setOpenUpdateModal(true);
            setUpdatedValues(buildPayload(values));
          } else {
            // Flat in the shared draft slice; the create modal routes `returns`
            // to the base input from there.
            // eslint-disable-next-line no-unused-vars
            const {baseFinancialInstrumentInput, ...flat} =
              buildPayload(values);
            dispatch(
              handleMutualFunds({
                ...flat,
                returns: baseFinancialInstrumentInput.returns,
              }),
            );
            setCreateModal(true);
          }
        }}
        form={form}
        layout="vertical">
        <FormCard
          title="Valuation"
          description="How often the fund is priced, and the return it projects">
          <div className={styles.flexed}>
            <Form.Item
              className={styles.smallWidth}
              label="Valuation Frequency"
              name="valuationFrequency">
              <Select placeholder="Daily" allowClear>
                <Select.Option value="DAILY">Daily</Select.Option>
                <Select.Option value="WEEKLY">Weekly</Select.Option>
                <Select.Option value="MONTHLY">Monthly</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              className={styles.smallWidth}
              name="settlementDaysOnRedemption"
              label="Settlement Days on Redemption">
              <InputNumber className={styles['input-number']} min={0} />
            </Form.Item>
          </div>
          <Form.Item
            name="projectedAnnualReturn"
            label="Projected Annual Return">
            <InputNumber
              className={styles['input-number']}
              placeholder="2,000,000"
              min={0}
              formatter={groupThousands}
              parser={parseThousands}
            />
          </Form.Item>
        </FormCard>

        <FormCard
          title="Trailing returns"
          description="Rolling windows measured back from today — never mixed with calendar years">
          <p className={styles.hint}>
            Rolling windows measured back from today — 1 month, YTD, since
            inception. Keep calendar years in the list below instead.
          </p>
          <Form.List name="trailingReturns">
            {(fields, {add, remove}) => (
              <>
                {fields.map(({key, name}) => (
                  <FadeEffect key={key}>
                    <div className={styles.returnRow}>
                      <Form.Item
                        rules={[{required: true, message: 'Required'}]}
                        label="Period"
                        name={[name, 'period']}>
                        <Select placeholder="Select a period">
                          {trailingReturnPeriods.map(period => (
                            <Select.Option
                              key={period.value}
                              value={period.value}>
                              {period.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        label="Metric"
                        name={[name, 'metric']}
                        initialValue="RETURN">
                        <Select>
                          {returnMetrics.map(metric => (
                            <Select.Option
                              key={metric.value}
                              value={metric.value}>
                              {metric.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        rules={[{required: true, message: 'Required'}]}
                        label="Value (%)"
                        name={[name, 'value']}>
                        <InputNumber className={styles.rowInput} />
                      </Form.Item>
                      <div className={styles.removeRow}>
                        <img
                          src={trash}
                          onClick={() => remove(name)}
                          alt="Remove"
                        />
                      </div>
                    </div>
                  </FadeEffect>
                ))}
                <div onClick={add} className={styles['add-item']}>
                  <img src={plusIcon} alt="" />
                  <span>Add Trailing Return</span>
                </div>
              </>
            )}
          </Form.List>
        </FormCard>

        <FormCard
          title="Calendar year returns"
          description="One entry per completed calendar year">
          <p className={styles.hint}>One entry per completed calendar year.</p>
          <Form.List name="calendarReturns">
            {(fields, {add, remove}) => (
              <>
                {fields.map(({key, name}) => (
                  <FadeEffect key={key}>
                    <div className={styles.returnRow}>
                      <Form.Item
                        rules={[{required: true, message: 'Required'}]}
                        label="Year"
                        name={[name, 'year']}>
                        <InputNumber
                          className={styles.rowInput}
                          min={1900}
                          max={2200}
                          placeholder="2025"
                        />
                      </Form.Item>
                      <Form.Item
                        label="Metric"
                        name={[name, 'metric']}
                        initialValue="RETURN">
                        <Select>
                          {returnMetrics.map(metric => (
                            <Select.Option
                              key={metric.value}
                              value={metric.value}>
                              {metric.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        rules={[{required: true, message: 'Required'}]}
                        label="Performance (%)"
                        name={[name, 'value']}>
                        <InputNumber className={styles.rowInput} />
                      </Form.Item>
                      <div className={styles.removeRow}>
                        <img
                          src={trash}
                          onClick={() => remove(name)}
                          alt="Remove"
                        />
                      </div>
                    </div>
                  </FadeEffect>
                ))}
                <div onClick={add} className={styles['add-item']}>
                  <img src={plusIcon} alt="" />
                  <span>Add Year</span>
                </div>
              </>
            )}
          </Form.List>
        </FormCard>

        <FormCard
          title="Investment terms"
          description="Horizon and the benefits shown to investors">
          <Form.Item
            className={styles.bigWidth}
            name="investmentHorizon"
            label="Investment Horizon">
            <Input />
          </Form.Item>
        </FormCard>

        <FormCard
          title="Benefits"
          description="Short lines describing what the investor gets">
          <Form.List name="benefits">
            {(fields, {add, remove}) => (
              <>
                {fields.map(({key, name}) => (
                  <FadeEffect key={key}>
                    <div className={v2.repeatRow}>
                      <Form.Item
                        rules={[
                          {required: true, message: 'This Field is Required'},
                        ]}
                        className={styles.bigWidth}
                        label={`${name + 1}. Benefits`}
                        name={[name, 'benefit']}>
                        <Input placeholder="Benefits" />
                      </Form.Item>
                      <img src={trash} onClick={() => remove(name)} alt="" />
                    </div>
                  </FadeEffect>
                ))}
                <div onClick={add} className={styles['add-item']}>
                  <img src={plusIcon} alt="" />
                  <span>Add Benefit</span>
                </div>
              </>
            )}
          </Form.List>
        </FormCard>

        <InstrumentActions
          onPrepopulate={() => form.setFieldsValue(FUND_INVESTMENT_MOCK)}
          disabled={!pageOperations?.update}
          label="Save and proceed"
        />
      </Form>
      <DBModal
        isOpen={createModal}
        handleClose={() => setCreateModal(false)}
        content={
          <CreateMutualFundsModal handleClose={() => setCreateModal(false)} />
        }
      />
      <ConfirmModal
        openModal={openUpdateModal}
        setOpenModal={() => setOpenUpdateModal(false)}
        loading={loadingUpdate}
        title="Update Mutual Fund"
        confirmText="Are you sure you want to update this fund?"
        handleOperation={() =>
          handleUpdateMutualFund(
            updateMutualFund,
            id,
            lang,
            updatedValues,
            refetch,
            setOpenUpdateModal,
            true,
          )
        }
      />
    </div>
  );
};
